import { AbPostHog } from 'app/abstract/services/posthog.abstract';
import { PostHogProperties } from './posthog-properties.types';
import { removerQueryDaUrl } from './posthog.config';

export const POSTHOG_EVENTO_PAGEVIEW = '$pageview';

export type PostHogLayoutMode = 'dss' | 'padrao';

/**
 * Propriedades do $pageview a partir de uma URL do Angular Router.
 *
 * A URL entra já sem query string e matrix params: rotas do produto carregam
 * identificadores de registro nesses trechos, e o evento é montado com a rota limpa
 * em vez de depender só do saneamento do SDK.
 */
export function montarPropriedadesPageview(
  url: string,
  layoutMode: PostHogLayoutMode
): PostHogProperties {
  const pathname = removerQueryDaUrl(url);
  return {
    $current_url: window.location.origin + window.location.pathname + '#' + pathname,
    $pathname: pathname,
    path: pathname,
    layout_mode: layoutMode
  };
}

/**
 * Emite o $pageview da rota. `capture_pageview` está desligado na config do SDK, então
 * cada host de rota (shell Angular e folha) é responsável por emitir o seu.
 */
export function capturarPageview(
  posthog: AbPostHog,
  url: string,
  layoutMode: PostHogLayoutMode
): void {
  posthog.capture(POSTHOG_EVENTO_PAGEVIEW, montarPropriedadesPageview(url, layoutMode));
}

/**
 * Emissor de $pageview com dedupe de rota, para hosts que recebem NavigationEnd repetido
 * para a rota corrente.
 *
 * O dedupe é do closure, e não do módulo, de propósito: voltar à mesma rota depois que o
 * host foi descartado é uma visita nova e precisa ser contada. Cada host cria o seu.
 *
 * Emissão é auxiliar e nunca pode derrubar a navegação de quem chama, então falha ao
 * resolver o serviço ou ao capturar só descarta o evento.
 */
export function criarEmissorPageview(
  posthog: AbPostHog | null,
  resolverLayoutMode: () => PostHogLayoutMode
): (url: string) => void {
  let ultimoPath: string | null = null;

  return (url: string) => {
    if (!posthog) {
      return;
    }
    const path = removerQueryDaUrl(url);
    if (path === ultimoPath) {
      return;
    }
    ultimoPath = path;
    try {
      capturarPageview(posthog, url, resolverLayoutMode());
    } catch (err) {
      console.warn('[PostHogService] $pageview descartado', err);
    }
  };
}
